import { useNavigate, type ErrorComponentProps } from '@tanstack/react-router';
import { SheetRouteError } from '@repo/ui/components/molecules/sheet-route-error';
import type { SheetSize } from '@repo/ui/components/molecules/sheet-view-wrapper';
import { SheetViewLoader } from './index';

interface SheetRouteErrorAtReturnUrlProps
  extends Pick<ErrorComponentProps, 'reset'> {
  returnUrl: string;
  size?: SheetSize;
}

/**
 * «Chiudi» torna a `returnUrl` conservando i filtri della lista. Esportato
 * per i pannelli disegnati fuori dall'`Outlet` (es. rapportini), che lo
 * usano come ripiego del proprio `CatchBoundary`.
 */
export function SheetRouteErrorAtReturnUrl({
  returnUrl,
  reset,
  size,
}: SheetRouteErrorAtReturnUrlProps) {
  const navigate = useNavigate();
  return (
    <SheetRouteError
      reset={reset}
      size={size}
      onClose={() => navigate({ to: returnUrl, search: (old) => old })}
    />
  );
}

/**
 * Creates common route options for sheet-based form routes.
 * Provides loading state with SheetViewLoader during route transitions and
 * keeps route errors inside the sheet (not the full-page fallback).
 *
 * @param returnUrl - URL to navigate back to when sheet is closed
 * @param options.size - larghezza della `SheetView` della rotta, che il
 *   pannello d'errore mantiene (default: larghezza storica del fallback)
 */
export const createSheetRouteOptions = (
  returnUrl: string,
  options: { size?: SheetSize } = {}
) => ({
  loader: () => void 0,
  pendingComponent: () => <SheetViewLoader returnUrl={returnUrl} />,
  errorComponent: ({ reset }: ErrorComponentProps) => (
    <SheetRouteErrorAtReturnUrl
      returnUrl={returnUrl}
      reset={reset}
      size={options.size}
    />
  ),
});
